<template>
  <v-sheet fluid class="pa-0 ma-0">
    <loading-spinner v-if="isLoading" class="py-10" />

    <v-card v-else class="ma-0 pa-0">
      <v-card-title class="ma-0 pa-3">
        <img
          :src="'/images/' + variable.category + '-icon-128.png'"
          :title="variable.category"
          style="height: 2.5em;"
          @error="missingDomainFallback"
        />
        <span class="ml-2 text-wrap"> {{ getVarName() }} </span>
        <v-spacer></v-spacer>
        <v-tooltip bottom color="primary">
          <template v-slot:activator="{ on: tooltip }">
            <v-btn
              icon
              class="white--text ma-0 pa-0"
              @click="close"
              v-on="{ ...tooltip }"
            >
              <v-icon color="black" center large class="pr-2">close</v-icon>
            </v-btn>
          </template>
          <span>Close</span>
        </v-tooltip>
      </v-card-title>

      <v-card-text class="ma-0 pa-0">
        <div v-if="getVarName() != variable.description" class="pl-5 pb-3">
          {{ variable.description }}
        </div>

        <v-container fluid class="pa-0 ma-0">
          <v-row class="pa-0 ma-0 mx-6 pb-2" justify="center" align="center">
            <v-tooltip
              v-if="longitudinal && isCollection"
              bottom
              color="primary"
            >
              <template v-slot:activator="{ on: tooltip }">
                <span v-on="{ ...tooltip }">
                  <v-checkbox
                    v-model="showAllVisits"
                    label="Show all visits"
                    class="pa-0 ma-0 mr-2"
                    dense
                    hide-details
                    :disabled="!isCollection || !firstVisit || !lastVisit"
                    @click="!showAllVisits"
                  />
                </span>
              </template>
              <span>Show all visits</span>
            </v-tooltip>

            <v-tooltip v-if="longitudinal" bottom color="primary">
              <template v-slot:activator="{ on: tooltip }">
                <span v-on="{ ...tooltip }">
                  <v-checkbox
                    v-model="hideOutlierVisits"
                    label="Hide outlier visits"
                    class="pa-0 ma-0"
                    dense
                    hide-details
                    :disabled="!showAllVisits"
                    @click="!hideOutlierVisits"
                  />
                </span>
              </template>
              <span>Hide visit(s) with less than 10% of subjects</span>
            </v-tooltip>

            <v-tooltip
              v-if="longitudinal && isCollection"
              bottom
              color="primary"
            >
              <template v-slot:activator="{ on: tooltip }">
                <span v-on="{ ...tooltip }">
                  <v-checkbox
                    v-model="highlightSetVisits"
                    label="Highlight set visits"
                    class="pa-0 ma-0 ml-2"
                    dense
                    hide-details
                    :disabled="!isCollection || !firstVisit || !lastVisit"
                    @click="!highlightSetVisits"
                  />
                </span>
              </template>
              <span>Highlight set first (green) and last (red) visits</span>
            </v-tooltip>

            <v-divider
              v-if="longitudinal && variable.data_category == 'Continuous'"
              class="ml-2 my-0"
              vertical
              inset
            ></v-divider>

            <span
              v-if="longitudinal && variable.data_category == 'Continuous'"
              class="subtitle-1 ml-2 black--text"
              >Show:</span
            >

            <v-checkbox
              v-if="longitudinal && variable.data_category == 'Continuous'"
              v-model="showViolinChart"
              label="Violin chart"
              class="pa-0 ma-0 ml-2"
              dense
              hide-details
              @click="!showViolinChart"
            />

            <v-checkbox
              v-if="longitudinal && variable.data_category == 'Continuous'"
              v-model="showBoxChart"
              label="Box chart"
              class="pa-0 ma-0 ml-2"
              dense
              hide-details
              @click="!showBoxChart"
            />

            <v-checkbox
              v-if="longitudinal && variable.data_category == 'Continuous'"
              v-model="showLineChart"
              label="Line chart"
              class="pa-0 ma-0 ml-2"
              dense
              hide-details
              @click="!showLineChart"
            />

            <v-checkbox
              v-if="longitudinal && variable.data_category == 'Continuous'"
              v-model="showOutliers"
              label="Outliers"
              class="pa-0 ma-0 ml-2"
              dense
              hide-details
              :disabled="!showViolinChart && !showBoxChart"
              @click="!showOutliers"
            />

            <v-checkbox
              v-if="longitudinal && variable.data_category == 'Continuous'"
              v-model="showYAxisFromZero"
              label="Y-axis from zero"
              class="pa-0 ma-0 ml-2"
              dense
              hide-details
              @click="!showYAxisFromZero"
            />
          </v-row>

          <v-row class="pa-0 ma-0 pr-4">
            <v-col cols="12" class="pa-0 ma-0">
              <PlotlyChart
                v-if="chartData.length > 0"
                :data="chartData"
                :layout="chartLayout"
                :display-mode-bar="true"
              />

              <v-sheet
                v-else
                class="display-1 primary--text text--lighten-5 pa-6"
                :style="{ height: chartHeight + 'px' }"
                align="center"
              >
                NO CHARTS SELECTED
              </v-sheet>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-card>
  </v-sheet>
</template>

<script>
import { mapState } from 'vuex';
import { state } from '@/store/modules/datasetSummary/types';
import { colors } from '@/utils/colors';
import { sortVisitEvents, missingDomainFallback } from '@/utils/helpers';
import { extent, mean } from 'd3-array';
import PlotlyChart from '@/components/common/PlotlyChart.vue';

export default {
  components: {
    PlotlyChart,
  },
  props: {
    variable: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isLoading: true,
      data: null,
      cache: {},
      visits: null,
      firstVisit: null,
      lastVisit: null,
      colors: colors,
      chartData: [],
      chartLayout: {},
      chartHeight: 500,
      // Longitudinal
      hideOutlierVisits: false,
      showAllVisits: true,
      highlightSetVisits: true,
      // Longitudinal continuous
      showViolinChart: false,
      showBoxChart: false,
      showLineChart: true,
      showOutliers: true,
      showYAxisFromZero: true,
      missingDomainFallback: missingDomainFallback,
    };
  },
  computed: {
    ...mapState('datasetSummary', {
      dataset: state.DATASET,
      collection: state.COLLECTION,
      isCollection: state.IS_COLLECTION,
      variableData: state.VARIABLE_DATA,
      visitVariable: state.VISIT_VARIABLE,
      useLongScaleNames: state.USE_LONG_SCALE_NAMES,
    }),
    longitudinal() {
      return (
        this.variable.type == 'observation' &&
        !!(this.isCollection
          ? this.collection.longitudinal
          : this.dataset.longitudinal)
      );
    },
    xAxisLabel() {
      if (this.longitudinal) {
        return 'Visits / Time';
      } else {
        return this.getVarName();
      }
    },
    yAxisLabel() {
      if (this.longitudinal) {
        return this.getVarName();
      } else {
        return 'Number of subjects';
      }
    },
  },
  watch: {
    variable() {
      this.initialize();
    },
    hideOutlierVisits() {
      if (this.showAllVisits) {
        this.visits = sortVisitEvents(Object.keys(this.data));

        if (this.hideOutlierVisits) {
          var cutoff =
            0.1 *
            (this.isCollection
              ? this.collection.num_subjects
              : this.dataset.num_subjects);

          this.visits = sortVisitEvents(
            Object.entries(this.data)
              .filter(([_, data]) => data.length >= cutoff)
              .map(([visit]) => visit)
          );
        }
      }

      if (this.variable.data_category == 'Continuous') {
        this.cache = {
          box: {
            x: [],
            y: [],
          },
          line: {
            x: [],
            y: [],
          },
        };

        for (const v of this.visits) {
          let d = this.data[v];
          this.cache['box']['x'].push(...Array(d.length).fill(v));
          this.cache['box']['y'].push(...d);
          this.cache['line']['x'].push(v);
          this.cache['line']['y'].push(mean(d));
        }
      } else {
        let groups = {};

        for (const v of this.visits) {
          for (const d of this.data[v]) {
            if (!groups[d]) {
              groups[d] = [];
            }
            let entry = groups[d].find(e => e.visit_event == v);
            if (entry) {
              entry.count += 1;
            } else {
              groups[d].push({ visit_event: v, count: 1 });
            }
          }
        }

        for (const [k, entries] of Object.entries(groups)) {
          this.cache[k] = {
            x: entries.map(e => e.visit_event),
            y: entries.map(e => e.count),
          };
        }
      }

      this.renderChart();
    },
    showAllVisits() {
      if (this.showAllVisits) {
        this.visits = sortVisitEvents(Object.keys(this.data));
      } else {
        this.visits = [this.firstVisit, this.lastVisit];
        this.hideOutlierVisits = false;
      }

      if (this.variable.data_category == 'Continuous') {
        this.cache = {
          box: {
            x: [],
            y: [],
          },
          line: {
            x: [],
            y: [],
          },
        };

        for (const v of this.visits) {
          let d = this.data[v];
          this.cache['box']['x'].push(...Array(d.length).fill(v));
          this.cache['box']['y'].push(...d);
          this.cache['line']['x'].push(v);
          this.cache['line']['y'].push(mean(d));
        }
      } else {
        let groups = {};

        for (const v of this.visits) {
          for (const d of this.data[v]) {
            if (!groups[d]) {
              groups[d] = [];
            }
            let entry = groups[d].find(e => e.visit_event == v);
            if (entry) {
              entry.count += 1;
            } else {
              groups[d].push({ visit_event: v, count: 1 });
            }
          }
        }

        for (const [k, entries] of Object.entries(groups)) {
          this.cache[k] = {
            x: entries.map(e => e.visit_event),
            y: entries.map(e => e.count),
          };
        }
      }

      this.renderChart();
    },
    highlightSetVisits() {
      this.renderChart();
    },
    showViolinChart() {
      this.renderChart();
    },
    showBoxChart() {
      this.renderChart();
    },
    showLineChart() {
      this.renderChart();
    },
    showOutliers() {
      this.renderChart();
    },
    showYAxisFromZero() {
      this.renderChart();
    },
  },
  created() {
    this.initialize();
  },
  methods: {
    async initialize() {
      this.isLoading = true;
      this.data = this.variableData[this.variable.type][this.variable.id];
      this.cache = {};
      var cutoff = null;

      if (!this.longitudinal) {
        this.data = this.flatten(this.data);

        if (this.variable.data_category == 'Continuous') {
          this.cache['x'] = this.data;
        } else {
          // compute unique and counts
          let res = this.data.reduce((acc, x) => {
            acc[x] = acc[x] + 1 || 1;
            return acc;
          }, {});
          this.cache['x'] = Object.keys(res);
          this.cache['y'] = Object.values(res);
        }
      } else {
        this.data = this.flattenAndGroupByVisit(this.data);

        if (this.isCollection) {
          if (this.visitVariable == 'Visit Event') {
            this.firstVisit = this.variable.first_visit_event;
            this.lastVisit = this.variable.last_visit_event;
          } else {
            this.firstVisit = this.variable.first_visit_num;
            this.lastVisit = this.variable.last_visit_num;
          }

          if (this.showAllVisits) {
            this.visits = sortVisitEvents(Object.keys(this.data));

            if (this.hideOutlierVisits) {
              cutoff =
                0.1 *
                (this.isCollection
                  ? this.collection.num_subjects
                  : this.dataset.num_subjects);

              this.visits = sortVisitEvents(
                Object.entries(this.data)
                  .filter(([_, data]) => data.length >= cutoff)
                  .map(([visit]) => visit)
              );
            }
          } else {
            this.visits = [this.firstVisit, this.lastVisit];
          }
        } else {
          this.visits = sortVisitEvents(Object.keys(this.data));

          if (this.hideOutlierVisits) {
            cutoff =
              0.1 *
              (this.isCollection
                ? this.collection.num_subjects
                : this.dataset.num_subjects);

            this.visits = sortVisitEvents(
              Object.entries(this.data)
                .filter(([_, data]) => data.length >= cutoff)
                .map(([visit]) => visit)
            );
          }
        }

        if (this.variable.data_category == 'Continuous') {
          this.cache = {
            box: {
              x: [],
              y: [],
            },
            line: {
              x: [],
              y: [],
            },
          };

          for (const v of this.visits) {
            let d = this.data[v];
            this.cache['box']['x'].push(...Array(d.length).fill(v));
            this.cache['box']['y'].push(...d);
            this.cache['line']['x'].push(v);
            this.cache['line']['y'].push(mean(d));
          }
        } else {
          let groups = {};

          for (const v of this.visits) {
            for (const d of this.data[v]) {
              if (!groups[d]) {
                groups[d] = [];
              }
              let entry = groups[d].find(e => e.visit_event == v);
              if (entry) {
                entry.count += 1;
              } else {
                groups[d].push({ visit_event: v, count: 1 });
              }
            }
          }

          for (const [k, entries] of Object.entries(groups)) {
            this.cache[k] = {
              x: entries.map(e => e.visit_event),
              y: entries.map(e => e.count),
            };
          }
        }

        if (this.hideOutlierVisits) {
          cutoff =
            0.1 *
            (this.isCollection
              ? this.collection.num_subjects
              : this.dataset.num_subjects);

          this.visits = sortVisitEvents(
            Object.entries(this.data)
              .filter(([_, data]) => data.length >= cutoff)
              .map(([visit]) => visit)
          );
        }
      }

      await this.renderChart();

      this.isLoading = false;
    },
    getVarName() {
      return this.useLongScaleNames
        ? this.variable.scale
        : this.variable.abbreviation;
    },
    flatten(data) {
      let res = [];
      if (data != null) {
        data.forEach(d => {
          res.push(d['value']);
        });
      }
      return res;
    },
    flattenAndGroupByVisit(data) {
      var visitKey =
        this.visitVariable == 'Visit Event' ? 'visit_event' : 'visit_num';
      var res = {};
      data.forEach(d => {
        var visit = d[visitKey];
        if (!res[visit]) {
          res[visit] = [];
        }
        res[visit].push(d['value']);
      });
      return res;
    },
    close() {
      this.$emit('close');
    },
    resetChart() {
      this.chartData = [];
      this.chartLayout = null;
    },
    async renderChart() {
      this.resetChart();

      // determine type of plot to render
      if (!this.longitudinal) {
        if (this.variable.data_category == 'Continuous') {
          this.renderHistogramChart();
        } else if (this.variable.data_category == 'Categorical') {
          this.renderBarChart();
        }
      } else {
        if (this.variable.data_category == 'Continuous') {
          if (this.showViolinChart) {
            this.renderViolinChart();
          } else if (this.showBoxChart) {
            this.renderBoxChart();
          } else if (this.showLineChart) {
            this.renderLineChart();
          }
        } else if (this.variable.data_category == 'Categorical') {
          this.renderGroupedBarChart();
        }
      }
    },
    renderHistogramChart() {
      this.chartData = [
        {
          x: this.cache['x'],
          histnorm: 'count',
          type: 'histogram',
          marker: {
            line: {
              color: 'white',
              width: 1,
            },
          },
          //xbins: { size: 10.0},
        },
      ];
      this.renderChartLayout();
    },
    renderBarChart() {
      this.chartData = [
        {
          x: this.cache['x'],
          y: this.cache['y'],
          type: 'bar',
        },
      ];

      this.renderChartLayout();
    },
    renderViolinChart() {
      this.chartData = [
        {
          x: this.cache['box']['x'],
          y: this.cache['box']['y'],
          type: 'violin',
          points: this.showOutliers,
          box: {
            visible: this.showBoxChart,
          },
          meanline: {
            visible: true,
          },
          line: {
            color: this.colors['plotly_palette'][0],
            width: 2,
          },
        },
      ];

      if (this.showLineChart) {
        this.chartData.push(this.getLineChartData());
      }

      this.renderChartLayout();
      this.renderSetVisitsHighlight();
    },
    renderBoxChart() {
      this.chartData = [
        {
          x: this.cache['box']['x'],
          y: this.cache['box']['y'],
          type: 'box',
          boxmean: true,
          boxpoints: this.showOutliers,
          line: {
            color: this.colors['plotly_palette'][0],
            width: 2,
          },
        },
      ];

      if (this.showLineChart) {
        this.chartData.push(this.getLineChartData());
      }

      this.renderChartLayout();
      this.renderSetVisitsHighlight();
    },
    getLineChartData() {
      return {
        x: this.cache['line']['x'],
        y: this.cache['line']['y'],
        type: 'scatter',
        mode: 'lines+markers',
        line: {
          color: this.colors['plotly_palette'][0],
          width: 3,
        },
        marker: {
          size: 12,
        },
      };
    },
    renderLineChart() {
      this.chartData = [this.getLineChartData()];
      this.renderChartLayout();
      this.renderSetVisitsHighlight();
    },
    renderGroupedBarChart() {
      for (const [k, v] of Object.entries(this.cache)) {
        let chartData = {
          x: v['x'],
          y: v['y'],
          name: k,
          type: 'bar',
          marker: {
            line: {
              color: 'white',
              width: 1,
            },
          },
        };

        this.chartData.push(chartData);
      }

      this.renderChartLayout({ stacked: true });
      this.renderSetVisitsHighlight();

      this.chartLayout['barmode'] = 'stack';
      this.chartLayout['showlegend'] = true;
    },
    // `stacked` is set by the one caller that stacks its traces; a stacked bar
    // is as tall as the sum of its segments, which the y-range below cannot
    // measure from the traces one at a time.
    renderChartLayout({ stacked = false } = {}) {
      let chartLayout = {
        showlegend: false,
        responsive: true,
        autosize: true,
        height: this.chartHeight,
        legend: {
          y: 0.95,
        },
        margin: {
          t: 30,
          l: 80,
          r: 40,
          b: 60,
        },
        yaxis: {
          zeroline: false,
          automargin: true,
          fixedrange: true,
          title: {
            text: this.yAxisLabel,
          },
        },
        xaxis: {
          zeroline: false,
          automargin: true,
          rangeslider: {
            thickness: 0.05,
            bgcolor: 'lightgray',
          },
          title: {
            text: this.xAxisLabel,
          },
        },
        hoverlabel: {
          bgcolor: '#ffffff',
          margin: {
            l: 10,
          },
          font: {
            family: 'Roboto, sans-serif',
            size: 16,
          },
        },
        font: {
          family: 'Roboto, sans-serif',
          size: 16,
        },
        modebar: {
          remove: ['lasso', 'select', 'pan'],
        },
      };
      // Stacked bars are already anchored at zero and plotly's own autorange
      // sizes them from the per-visit totals, so the manual range below -- which
      // reads each trace separately -- would only ever cut the taller bars off.
      if (this.showYAxisFromZero && !stacked) {
        let yext = null;
        this.chartData.forEach(cd => {
          if ('y' in cd) {
            let nyext = extent(cd['y'].map(y => y * 1.0));
            if (yext == null) {
              yext = nyext;
            } else {
              if (nyext[0] < yext[0]) {
                yext[0] = nyext[0];
              }
              if (nyext[1] > yext[1]) {
                yext[1] = nyext[1];
              }
            }
          }
        });
        if (yext != null && yext[0] > 0) {
          var new_ymax = yext[1] * 1.0 + (yext[1] - yext[0]) * 0.25;
          if (new_ymax > 0) {
            chartLayout['yaxis']['range'] = [0, new_ymax];
          }
        }
      }
      this.chartLayout = chartLayout;
    },
    renderSetVisitsHighlight() {
      if (
        this.highlightSetVisits &&
        this.isCollection &&
        this.firstVisit &&
        this.lastVisit
      ) {
        var firstVisitIdx = this.visits.indexOf(this.firstVisit);
        var firstVisit = {
          type: 'rect',
          xref: 'x',
          yref: 'paper',
          x0: firstVisitIdx - 0.5,
          x1: firstVisitIdx + 0.5,
          y0: 1,
          y1: 0,
          layer: 'below',
          fillcolor: this.colors['firstVisit'],
          opacity: this.colors['firstVisit-opacity'],
          line: {
            width: 0,
          },
        };

        var lastVisitIdx = this.visits.indexOf(this.lastVisit);
        var lastVisit = {
          type: 'rect',
          xref: 'x',
          yref: 'paper',
          x0: lastVisitIdx - 0.5,
          x1: lastVisitIdx + 0.5,
          y0: 1,
          y1: 0,
          layer: 'below',
          fillcolor: this.colors['lastVisit'],
          opacity: this.colors['lastVisit-opacity'],
          line: {
            width: 0,
          },
        };

        this.chartLayout['shapes'] = [firstVisit, lastVisit];
      }
    },
  },
};
</script>

<style scoped>
.v-divider {
  border-color: gray !important;
}
</style>
